import { useEffect, useRef } from 'react';
import API_ENDPOINTS from '../Config/api';
import { clearSessionAndRedirect } from '../Utils/logoutRedirect';

/**
 * Hook that proactively refreshes the JWT access token before it expires.
 * 
 * - Runs a timer every REFRESH_INTERVAL_MS (8 min) while the tab is visible
 * - Pauses when the tab is hidden (user switched away)
 * - Resumes immediately when the tab becomes visible again
 * - On refresh failure (refresh token expired), forces logout + redirect to login
 * 
 * Mount this once inside Layout (or any top-level authenticated component).
 */

const REFRESH_INTERVAL_MS = 8 * 60 * 1000; // 8 minutes (token expires in 10)

export default function useTokenRefresh() {
  const intervalRef = useRef(null);

  useEffect(() => {
    function doRefresh() {
      const refresh = sessionStorage.getItem('refresh_token');
      if (!refresh) return;

      fetch(API_ENDPOINTS.refreshToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      })
        .then(res => {
          if (!res.ok) {
            // Refresh token expired or invalid — force logout
            forceLogoutAndRedirect();
            return null;
          }
          return res.json();
        })
        .then(data => {
          if (!data) return;
          if (data.access) {
            sessionStorage.setItem('access_token', data.access);
          }
          if (data.refresh) {
            sessionStorage.setItem('refresh_token', data.refresh);
          }
        })
        .catch(() => {
          // Network error — don't logout, will retry on next interval
        });
    }

    function startInterval() {
      if (intervalRef.current) return; // Already running
      intervalRef.current = setInterval(doRefresh, REFRESH_INTERVAL_MS);
    }

    function stopInterval() {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // Tab became visible — refresh immediately + restart timer
        doRefresh();
        startInterval();
      } else {
        // Tab hidden — stop refreshing
        stopInterval();
      }
    }

    // Start on mount
    startInterval();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}

function forceLogoutAndRedirect() {
  clearSessionAndRedirect();
}
